import { MapItemSUP } from "@/types/search";
import { useCallback, useRef } from "react";
import { useSearchPageInteraction } from "../index";
interface MarkerData {
  coordinates: [number, number];
  projectId: string;
  projectName: string;
  sups?: MapItemSUP[];
}

/**
 * Optimized clicked state management with minimal re-renders
 */
export function useOptimizedClicked(itemId: string | null) {
  const addSelectedMarker = useSearchPageInteraction(state => state.addSelectedMarker);
  const removeSelectedMarker = useSearchPageInteraction(state_0 => state_0.removeSelectedMarker);

  // Select ONLY the boolean status for this specific item to prevent unnecessary re-renders
  // when other items are selected/deselected
  const isClicked = useSearchPageInteraction(useCallback(state_1 => itemId ? !!state_1.selectedMarkers[itemId] : false, [itemId]));

  // Use a ref to track clicked state to prevent re-renders during interactions
  // This ref is used inside handleToggleClick to avoid adding isClicked to dependency array
  const isClickedRef = useRef(isClicked);
  isClickedRef.current = isClicked;
  const handleToggleClick = useCallback((data?: MarkerData) => {
    if (!itemId) return;
    const wasClicked = isClickedRef.current;
    if (wasClicked) {
      removeSelectedMarker(itemId);
      isClickedRef.current = false;
    } else {
      // Use provided data or create basic structure
      const markerData: MapItemSUP = data ? data as unknown as MapItemSUP : {
        _id: itemId,
        coordinates: itemId.split(",").map(Number) as [number, number],
        projectId: "",
        projectName: ""
      } as MapItemSUP;
      addSelectedMarker(itemId, markerData);
      isClickedRef.current = true;
    }
  }, [itemId, addSelectedMarker, removeSelectedMarker]);
  return {
    isClicked,
    handleToggleClick
  };
}